/**
 * /admin/vision-stats — vision_extract llm_runs 의 (promptVersion, status) group 통계.
 *
 * 용도: admin daily 모니터링. 특히 batch-retry 의 신규 promptVersion
 *   (v5-batch-discarded, v5-batch-retry-discarded) 자연 발생 추적.
 */
import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ErrorBox } from './Watchlist';

type WindowKey = '24h' | '7d' | '30d' | 'all';
const WINDOW_LABEL: Record<WindowKey, string> = {
  '24h': '24시간',
  '7d': '7일',
  '30d': '30일',
  all: '전체',
};
const WINDOWS: WindowKey[] = ['24h', '7d', '30d', 'all'];

type PurposeKey =
  | 'vision_extract'
  | 'report_extract'
  | 'section_update'
  | 'quarter_projection'
  | 'grounded_rag_answer'
  | 'all';
const PURPOSE_LABEL: Record<PurposeKey, string> = {
  vision_extract: 'Vision OCR',
  report_extract: 'Report Extract',
  section_update: 'Section Update',
  quarter_projection: 'Wiki Projection',
  grounded_rag_answer: 'RAG',
  all: '전체',
};
const PURPOSES: PurposeKey[] = [
  'vision_extract',
  'report_extract',
  'section_update',
  'quarter_projection',
  'grounded_rag_answer',
  'all',
];

const BATCH_RETRY_VERSIONS = new Set([
  'v5-batch-discarded',
  'v5-batch-retry-discarded',
  'v5-batch-retry',
]);

function fmtCost(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.01) return `${(usd * 100).toFixed(2)}¢`;
  return `$${usd.toFixed(4)}`;
}

function fmtMs(ms: number): string {
  if (ms === 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const days = Math.floor(diff / 86_400_000);
  if (days >= 1) return `${days}일 전`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours >= 1) return `${hours}h 전`;
  const mins = Math.floor(diff / 60_000);
  return `${mins}m 전`;
}

export default function AdminVisionStatsPage() {
  const [window, setWindow] = useState<WindowKey>('7d');
  const [purpose, setPurpose] = useState<PurposeKey>('vision_extract');
  const { data, error, loading } = useAsync(
    () => api.visionStats(purpose, window),
    [purpose, window],
  );

  if (error) return <ErrorBox msg={error} />;

  const items = data?.items ?? [];
  const totalCost = items.reduce((s, r) => s + (r.costUsd ?? 0), 0);
  const totalCount = items.reduce((s, r) => s + (r.count ?? 0), 0);
  const batchRetrySeen =
    purpose === 'vision_extract' && items.some((r) => BATCH_RETRY_VERSIONS.has(r.promptVersion));
  const showPurposeCol = purpose === 'all';

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">📊 LLM Stats</h1>
        <p className="text-sm text-slate-500">
          {totalCount} calls · {fmtCost(totalCost)}
        </p>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        llm_runs 의 (purpose, prompt_version, status) group 통계. purpose 선택 가능.
      </p>

      {/* Purpose selector */}
      <div className="mt-4 flex items-center gap-2 text-sm">
        <span className="text-slate-500 w-12">단계:</span>
        {PURPOSES.map((p) => (
          <button
            key={p}
            onClick={() => setPurpose(p)}
            className={`rounded-md px-3 py-1.5 ${
              p === purpose ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {PURPOSE_LABEL[p]}
          </button>
        ))}
      </div>

      {/* Window selector */}
      <div className="mt-2 flex items-center gap-2 text-sm">
        <span className="text-slate-500 w-12">기간:</span>
        {WINDOWS.map((w) => (
          <button
            key={w}
            onClick={() => setWindow(w)}
            className={`rounded-md px-3 py-1.5 ${
              w === window ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {WINDOW_LABEL[w]}
          </button>
        ))}
      </div>

      {/* batch-retry callout — vision_extract 일 때만. */}
      {purpose === 'vision_extract' && (
        <div
          className={`mt-4 rounded-md border px-4 py-2 text-sm ${
            batchRetrySeen
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : 'border-slate-200 bg-slate-50 text-slate-600'
          }`}
        >
          batch-retry:{' '}
          {batchRetrySeen
            ? '자연 발생 ✓ — 신코드 trigger 확인됨'
            : '0건 — broken case 미발생 (드물어서 정상). 자연 trigger 대기.'}
        </div>
      )}

      {loading && <p className="mt-4 text-slate-500">로딩 중…</p>}

      {/* Stats table */}
      {!loading && (
        <table className="mt-5 w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {showPurposeCol && <th className="py-2 pr-3">purpose</th>}
              <th className="py-2 pr-3">promptVersion</th>
              <th className="pr-3">status</th>
              <th className="pr-3 text-right">count</th>
              <th className="pr-3 text-right">cost</th>
              <th className="pr-3 text-right">avg in</th>
              <th className="pr-3 text-right">avg out</th>
              <th className="pr-3 text-right">avg latency</th>
              <th>last run</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.map((r) => {
              const isBatchRetry =
                r.purpose === 'vision_extract' && BATCH_RETRY_VERSIONS.has(r.promptVersion);
              return (
                <tr
                  key={`${r.purpose}/${r.promptVersion}/${r.status}`}
                  className={isBatchRetry ? 'bg-emerald-50' : ''}
                >
                  {showPurposeCol && (
                    <td className="py-2 pr-3 font-mono text-xs text-slate-600">{r.purpose}</td>
                  )}
                  <td className="py-2 pr-3 font-mono text-xs">
                    {r.promptVersion}
                    {isBatchRetry && <span className="ml-1.5 text-emerald-600">★</span>}
                  </td>
                  <td className="pr-3 text-xs">{r.status}</td>
                  <td className="pr-3 text-right font-mono text-xs">{r.count}</td>
                  <td className="pr-3 text-right font-mono text-xs">{fmtCost(r.costUsd)}</td>
                  <td className="pr-3 text-right font-mono text-xs text-slate-500">{r.avgInputTokens.toLocaleString()}</td>
                  <td className="pr-3 text-right font-mono text-xs text-slate-500">{r.avgOutputTokens.toLocaleString()}</td>
                  <td className="pr-3 text-right font-mono text-xs text-slate-500">{fmtMs(r.avgLatencyMs)}</td>
                  <td className="text-xs text-slate-500" title={r.lastRun}>{fmtRelative(r.lastRun)}</td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={showPurposeCol ? 9 : 8} className="py-6 text-center text-slate-500">
                  해당 기간의 {PURPOSE_LABEL[purpose]} 실행 없음.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
